import { useCallback, useEffect, useState } from "react";

import { type Question, QUESTIONS, type QuestionStats, type Stats, percentage } from "../../shared";
import { useLiveStats } from "../survey";
import { AnimatedNumber, Bump, LivePill, Logo, cx, fillStyle, useBumps } from "../ui";
import { WEBINAR } from "../webinar";
import { QrCode } from "./QrCode";

const AUTOPLAY_MS = 12_000;
const OVERVIEW_HASH = "#uebersicht";

function indexFromHash(): number {
	const index = Number.parseInt(window.location.hash.slice(1), 10) - 1;
	return Number.isInteger(index) && index >= 0 && index < QUESTIONS.length ? index : 0;
}

function leadingVotes(question: Question, stats: QuestionStats | undefined): number {
	return Math.max(0, ...question.options.map((option) => stats?.counts[option.id] ?? 0));
}

function ResultRows({ question, stats }: { question: Question; stats: QuestionStats | undefined }) {
	const bumps = useBumps(stats?.counts);
	const leader = leadingVotes(question, stats);

	return (
		<ul className="grid gap-3.5 [@media(min-height:60rem)]:gap-5">
			{question.options.map((option) => {
				const votes = stats?.counts[option.id] ?? 0;
				return (
					<li key={option.id} className="grid grid-cols-[minmax(0,1fr)_auto] items-end gap-x-6 gap-y-2">
						<p className="flex items-center gap-3 text-lg font-medium text-white xl:text-xl">
							<span className="text-2xl leading-none" aria-hidden="true">
								{option.emoji}
							</span>
							{option.label}
						</p>
						<p className="relative text-right leading-none text-white">
							<span className="text-2xl font-bold xl:text-3xl">
								<AnimatedNumber value={percentage(stats, option.id)} /> %
							</span>
							<span className="ml-2 text-sm text-ice/70">
								<AnimatedNumber value={votes} />
							</span>
							<Bump token={bumps[option.id]} className="-top-6 right-0 text-turquoise-light" />
						</p>
						<div className="result-track col-span-2 h-3.5">
							<span
								className="result-fill"
								data-leading={votes > 0 && votes === leader ? "" : undefined}
								style={fillStyle(percentage(stats, option.id))}
							/>
						</div>
					</li>
				);
			})}
		</ul>
	);
}

function ResultColumns({ question, stats }: { question: Question; stats: QuestionStats | undefined }) {
	const bumps = useBumps(stats?.counts);
	const leader = leadingVotes(question, stats);

	return (
		// Shared rows keep every bar on the same baseline. A longer label such as
		// "Chaos mit System" would otherwise lift its own column.
		<ul className="grid h-full grid-cols-5 grid-rows-[auto_minmax(13rem,1fr)_auto_auto] content-end gap-x-3 gap-y-3 sm:gap-x-4 xl:grid-rows-[auto_minmax(18rem,1fr)_auto_auto] xl:gap-x-8">
			{question.options.map((option) => {
				const votes = stats?.counts[option.id] ?? 0;
				return (
					<li key={option.id} className="relative row-span-4 grid min-w-0 grid-cols-1 grid-rows-subgrid justify-items-center">
						<Bump token={bumps[option.id]} className="-top-6 text-turquoise-light" />
						<p className="self-end text-center text-xl leading-none font-bold whitespace-nowrap text-white sm:text-2xl xl:text-3xl">
							<AnimatedNumber value={percentage(stats, option.id)} /> %
						</p>
						<div className="result-track h-full w-full min-w-0 max-w-32">
							<span
								className="result-column-fill"
								data-leading={votes > 0 && votes === leader ? "" : undefined}
								style={fillStyle(leader ? (votes / leader) * 100 : 0)}
							/>
						</div>
						<span className="self-end text-4xl leading-none" aria-hidden="true">
							{option.emoji}
						</span>
						<p className="self-start text-center text-sm leading-snug font-medium text-ice xl:text-base">{option.label}</p>
					</li>
				);
			})}
		</ul>
	);
}

type QuestionPanelProps = {
	index: number;
	stats: Stats;
	onSelect: (index: number) => void;
};

function QuestionPanel({ index, stats, onSelect }: QuestionPanelProps) {
	const question = QUESTIONS[index];
	if (!question) {
		return null;
	}
	const questionStats = stats.questions[question.id];
	const respondents = questionStats?.respondents ?? 0;

	return (
		<section className="deck starfield flex flex-col rounded-[2.25rem] p-8 lg:p-10">
			<div className="flex flex-wrap items-center justify-between gap-3">
				<span className="deck-label">
					Frage {index + 1} von {QUESTIONS.length} · {question.kicker}
				</span>
				<span className="text-sm text-ice">
					<AnimatedNumber value={respondents} /> {respondents === 1 ? "Antwort" : "Antworten"}
					{question.kind === "multi" && " · Mehrfachauswahl"}
				</span>
			</div>

			<h1 key={question.id} className="screen-on mt-6 text-[clamp(1.875rem,3.2vw,3.25rem)] leading-[1.08] font-bold tracking-tight text-white">
				{question.title}
			</h1>

			<div key={`results-${question.id}`} className="screen-on mt-8 flex-1">
				{question.kind === "scale" ? (
					<ResultColumns question={question} stats={questionStats} />
				) : (
					<ResultRows question={question} stats={questionStats} />
				)}
				{respondents === 0 && (
					<p className="mt-6 text-center text-lg text-ice">
						Noch keine Antworten – jetzt QR-Code scannen und als Erste:r abstimmen!
					</p>
				)}
			</div>

			<nav className="mt-8 flex items-center justify-between gap-4" aria-label="Fragen">
				<button type="button" className="btn-deck" disabled={index === 0} onClick={() => onSelect(index - 1)}>
					← Zurück
				</button>
				<div className="flex flex-wrap justify-center gap-2">
					{QUESTIONS.map((item, i) => (
						<button
							key={item.id}
							type="button"
							className={cx("h-2.5 rounded-full", i === index ? "w-8 bg-turquoise-light" : "w-2.5 bg-white/25 hover:bg-white/50")}
							aria-label={`Frage ${i + 1}: ${item.short}`}
							aria-current={i === index ? "step" : undefined}
							onClick={() => onSelect(i)}
						/>
					))}
				</div>
				<button
					type="button"
					className="btn-deck"
					disabled={index === QUESTIONS.length - 1}
					onClick={() => onSelect(index + 1)}
				>
					Weiter →
				</button>
			</nav>
		</section>
	);
}

function Overview({ stats, onOpen }: { stats: Stats; onOpen: (index: number) => void }) {
	return (
		<section className="grid content-start gap-5 md:grid-cols-2 2xl:grid-cols-4">
			{QUESTIONS.map((question, index) => {
				const questionStats = stats.questions[question.id];
				const top = [...question.options]
					.sort((a, b) => (questionStats?.counts[b.id] ?? 0) - (questionStats?.counts[a.id] ?? 0))
					.slice(0, 3);
				return (
					<button
						key={question.id}
						type="button"
						className="surface-card flex flex-col p-5 text-left hover:border-primary/40"
						onClick={() => onOpen(index)}
					>
						<span className="text-xs font-medium text-muted-foreground">
							{index + 1} · {question.kicker} · {questionStats?.respondents ?? 0} Antworten
						</span>
						<span className="mt-1 text-lg font-semibold">{question.short}</span>
						<ul className="mt-4 grid gap-3">
							{top.map((option) => (
								<li key={option.id} className="min-w-0">
									<div className="flex items-baseline justify-between gap-3 text-sm">
										<span className="truncate">
											{option.emoji} {option.label}
										</span>
										<span className="shrink-0 font-semibold whitespace-nowrap tabular-nums">
											{percentage(questionStats, option.id)} %
										</span>
									</div>
									<div className="light-track mt-1.5">
										<span className="light-fill" style={fillStyle(percentage(questionStats, option.id))} />
									</div>
								</li>
							))}
						</ul>
					</button>
				);
			})}
		</section>
	);
}

function JoinCard({ participants, completed }: { participants: number; completed: number }) {
	const url = `${window.location.origin}/`;

	return (
		<section className="surface-card p-6 text-center">
			<span className="eyebrow">Jetzt mitmachen</span>
			<QrCode value={url} className="mx-auto mt-5 w-full max-w-60 rounded-lg" />
			<p className="mt-4 text-lg font-semibold break-all">{window.location.host}</p>
			<p className="mt-1 text-sm text-muted-foreground">Handykamera draufhalten oder Adresse eintippen.</p>
			<dl className="mt-6 grid grid-cols-2 border-t border-border pt-5">
				<div>
					<dt className="text-xs text-muted-foreground">an Bord</dt>
					<dd className="text-4xl font-bold text-primary">
						<AnimatedNumber value={participants} />
					</dd>
				</div>
				<div className="border-l border-border">
					<dt className="text-xs text-muted-foreground">gelandet</dt>
					<dd className="text-4xl font-bold text-primary">
						<AnimatedNumber value={completed} />
					</dd>
				</div>
			</dl>
		</section>
	);
}

export function Presenter() {
	const { connected, stats } = useLiveStats();
	const [index, setIndex] = useState(indexFromHash);
	const [overview, setOverview] = useState(() => window.location.hash === OVERVIEW_HASH);
	const [autoplay, setAutoplay] = useState(false);

	const select = useCallback((next: number) => {
		setIndex(Math.min(Math.max(next, 0), QUESTIONS.length - 1));
		setOverview(false);
	}, []);

	useEffect(() => {
		window.history.replaceState(null, "", overview ? OVERVIEW_HASH : `#${index + 1}`);
	}, [index, overview]);

	useEffect(() => {
		if (!autoplay || overview) {
			return;
		}
		const timer = window.setInterval(() => setIndex((current) => (current + 1) % QUESTIONS.length), AUTOPLAY_MS);
		return () => window.clearInterval(timer);
	}, [autoplay, overview]);

	useEffect(() => {
		// Arrow keys plus PageUp/PageDown, which is what presentation clickers send.
		const onKeyDown = (event: KeyboardEvent) => {
			if (event.altKey || event.ctrlKey || event.metaKey) {
				return;
			}
			switch (event.key) {
				case "ArrowRight":
				case "PageDown":
					event.preventDefault();
					setOverview(false);
					setIndex((current) => Math.min(current + 1, QUESTIONS.length - 1));
					break;
				case "ArrowLeft":
				case "PageUp":
					event.preventDefault();
					setOverview(false);
					setIndex((current) => Math.max(current - 1, 0));
					break;
				case "o":
					setOverview((current) => !current);
					break;
				case "a":
					setAutoplay((current) => !current);
					break;
				case "f":
					if (document.fullscreenElement) {
						void document.exitFullscreen();
					} else {
						void document.documentElement.requestFullscreen();
					}
					break;
			}
		};
		window.addEventListener("keydown", onKeyDown);
		return () => window.removeEventListener("keydown", onKeyDown);
	}, []);

	return (
		<div className="flex min-h-dvh flex-col">
			<header className="mx-auto flex w-full max-w-[1600px] flex-wrap items-center justify-between gap-4 px-6 pt-6 pb-5 lg:px-10">
				<div className="flex items-center gap-5">
					<Logo className="h-8 w-auto" />
					<span className="hidden text-sm text-muted-foreground md:inline">
						Live-Umfrage · {WEBINAR.host} · {WEBINAR.date}
					</span>
				</div>
				<LivePill connected={connected} online={stats.online} />
			</header>

			<main className="mx-auto grid w-full max-w-[1600px] flex-1 gap-6 px-6 pb-8 lg:grid-cols-[minmax(0,1fr)_20rem] lg:px-10">
				{overview ? (
					<Overview stats={stats} onOpen={select} />
				) : (
					<QuestionPanel index={index} stats={stats} onSelect={select} />
				)}

				<aside className="grid content-start gap-6">
					<JoinCard participants={stats.participants} completed={stats.completed} />
					<section className="surface-card grid gap-2 p-5">
						<button type="button" className="btn-secondary w-full" onClick={() => setOverview((v) => !v)}>
							{overview ? "Einzelne Frage" : "Übersicht"} <kbd className="text-xs text-muted-foreground">O</kbd>
						</button>
						<button type="button" className="btn-secondary w-full" onClick={() => setAutoplay((v) => !v)}>
							{autoplay ? "Auto-Wechsel stoppen" : "Auto-Wechsel starten"}{" "}
							<kbd className="text-xs text-muted-foreground">A</kbd>
						</button>
						<p className="mt-1 text-center text-xs text-muted-foreground">
							← → oder Presenter wechseln die Frage · F für Vollbild
						</p>
					</section>
				</aside>
			</main>
		</div>
	);
}
